import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WorkoutStartContext } from './WorkoutStartContext';
import { isForeground, subscribeForeground } from '../../services/AppStateService';

const RECONCILE_DELAYS = [1000, 2000, 4000, 8000];

const live = activity => activity?.type === 'workout' && ['ongoing', 'in_progress', 'started'].includes(activity.status);
export const activityVersion = activity => Math.max(0, ...['updated_at', 'workout_stopped_at', 'completed_at', 'started_at'].map(key => new Date(activity?.[key] || 0).getTime()).filter(Number.isFinite));

// Revision authority precedes lifecycle timestamps. A tie is inconclusive: it
// cannot prove a stop happened after the acknowledged start.
export function compareActivityLifecycle(incoming, acknowledged) {
  const revision = row => {
    const value = row?.user_activity_revision;
    return (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))
      && Number.isFinite(Number(value)) ? Number(value) : null;
  };
  const incomingRevision = revision(incoming);
  const knownRevision = revision(acknowledged);
  if (knownRevision !== null && incomingRevision === null) return -1;
  if (knownRevision !== null && incomingRevision !== null && incomingRevision !== knownRevision) {
    return incomingRevision > knownRevision ? 1 : -1;
  }
  return Math.sign(activityVersion(incoming) - activityVersion(acknowledged));
}

// Preserve an acknowledged lifecycle against older full-row reads, but allow a
// later stop/completion/edit. This is not an optimistic "started" timestamp.
export function preferAcknowledgedActivity(incoming, acknowledged) {
  if (!acknowledged || String(incoming?._id) !== String(acknowledged._id)) return incoming;
  const order = compareActivityLifecycle(incoming, acknowledged);
  if (order < 0) return acknowledged;
  if (order === 0 && ((acknowledged.status === 'complete' && incoming.status !== 'complete')
    || (incoming.status === 'pending' && acknowledged.status !== 'pending'))) return acknowledged;
  return incoming;
}

export function useWorkoutStart({ activity, backendService, videoService, onActivityRefresh, setStreamNotice: onStreamNotice, readOnly, suppressActions, position, gymTrackingEnabled }) {
  const coordinator = useContext(WorkoutStartContext);
  const [, render] = useState(0);
  const localScope = useRef(null);
  const owner = backendService.positionOwner;
  const identity = backendService.authenticatedIdentity;
  const key = [activity._id, backendService, owner, identity, readOnly, suppressActions, coordinator];
  if (!localScope.current || key.some((value, index) => value !== localScope.current.key[index])) {
    if (!coordinator && localScope.current) localScope.current.scope.active = false;
    const create = () => ({ active: true, generation: 0, acknowledged: null, phase: 'idle', notice: null,
      operationRef: { current: null }, checkingRef: { current: false }, listeners: new Set() });
    localScope.current = { key, scope: coordinator ? coordinator.operation(activity._id, create) : create() };
  }
  const scope = localScope.current.scope;
  const { operationRef, checkingRef, acknowledged, phase, notice } = scope;
  const update = (field, value) => {
    scope[field] = value;
    scope.listeners.forEach(listener => listener());
  };
  const setAcknowledged = value => update('acknowledged', value);
  const setPhase = value => update('phase', value);
  const setNotice = value => update('notice', value);
  const setStreamNotice = value => update('streamNotice', value);
  useLayoutEffect(() => {
    // StrictMode replays setup/cleanup; reactivate only this exact scope.
    scope.active = true;
    const listener = () => {
      if (scope.streamNotice !== undefined) onStreamNotice(scope.streamNotice);
      render(value => value + 1);
    };
    scope.listeners.add(listener);
    listener();
    return () => {
      scope.listeners.delete(listener);
      if (!coordinator) scope.active = false;
    };
  }, [scope, coordinator, onStreamNotice]);
  useLayoutEffect(() => {
    if (readOnly || suppressActions) { scope.active = false; scope.generation += 1; }
    else scope.active = true;
  }, [scope, readOnly, suppressActions]);
  useLayoutEffect(() => {
    if (acknowledged && activity.status === 'pending' && compareActivityLifecycle(activity, acknowledged) > 0) {
      operationRef.current = null;
      scope.generation += 1;
      checkingRef.current = false;
      scope.acknowledged = null; scope.phase = 'idle'; scope.notice = null;
      scope.listeners.forEach(listener => listener());
    }
  }, [activity, acknowledged, scope, operationRef, checkingRef]);

  const current = scope => scope?.active && (!coordinator || (coordinator.isActive() && !coordinator.isDeleted(activity._id))) && backendService.positionOwner === owner && backendService.authenticatedIdentity === identity;
  const publish = (row, scope) => {
    const generation = scope.generation;
    const isCurrent = () => current(scope) && scope.generation === generation;
    if (!isCurrent()) return;
    scope.acknowledged = row;
    setAcknowledged(row);
    setPhase('idle');
    // Parent reconciliation is not part of the write. Its failure cannot invite
    // another start or hide the server-acknowledged editor.
    try {
      Promise.resolve(onActivityRefresh?.(row, { workoutStart: true })).catch(() => {
        if (isCurrent()) setNotice('Workout started. Feed refresh failed; refresh workout to check its latest state.');
      });
    } catch (_) {
      setNotice('Workout started. Feed refresh failed; refresh workout to check its latest state.');
    }
  };
  const check = async (scope = localScope.current.scope, automatic = false) => {
    if (!current(scope) || checkingRef.current) return;
    if (automatic && (scope.autoAttempts || 0) >= RECONCILE_DELAYS.length) return;
    // Admit the budget at the same shared lock as the actual read. A manual
    // read cancels pending backoff; waiters resume only when that read settles.
    scope.autoTimer?.cancel();
    scope.autoRearmAt = null;
    if (automatic) scope.autoAttempts = (scope.autoAttempts || 0) + 1;
    const generation = scope.generation;
    const isCurrent = () => current(scope) && scope.generation === generation;
    checkingRef.current = true;
    setPhase('checking');
    try {
      const row = await backendService.fetchActivity(activity._id, { timeout: 15000 });
      if (!isCurrent()) return;
      if (String(row?._id) !== String(activity._id) || row.type !== 'workout') throw new Error('Incomplete workout read');
      const known = scope.acknowledged;
      if (known && preferAcknowledgedActivity(row, known) !== row) throw new Error('Older or inconclusive workout read');
      if (!live(row) && !['pending', 'complete'].includes(row.status)) throw new Error('Unsupported workout status');
      if (!known && !live(row) && row.status !== 'complete') {
        // A read cannot prove an earlier timed-out write will not still commit.
        setPhase('unknown');
        setNotice('Start outcome is still unknown. Check workout status before starting again.');
        return;
      }
      publish(row, scope);
      if (row.status === 'pending') operationRef.current = null;
      setNotice(null);
    } catch (_) {
      if (!isCurrent()) return;
      setPhase(scope.acknowledged ? 'idle' : 'unknown');
      setNotice(scope.acknowledged
        ? 'Workout started. Latest state could not be refreshed; your saved start is kept.'
        : 'Start outcome is unknown. Check workout status before starting again.');
    } finally {
      if (isCurrent()) {
        checkingRef.current = false;
        scope.recoveryListeners?.forEach(listener => listener());
      }
    }
  };
  const start = async event => {
    event.stopPropagation();
    const scope = localScope.current.scope;
    if (!current(scope) || operationRef.current || readOnly || suppressActions || activity.status !== 'pending') return;
    const generation = scope.generation;
    const isCurrent = () => current(scope) && scope.generation === generation;
    operationRef.current = scope;
    scope.autoAttempts = 0;
    scope.autoRearmAt = null;
    scope.autoResumedAt = Date.now();
    setPhase('starting'); setNotice(null);
    try {
      // No GPS prompt or awaited device fix: only separately consented watcher data.
      const response = await backendService.startWorkout(activity._id, gymTrackingEnabled ? position || null : null);
      if (!isCurrent()) return;
      if (String(response?._id) !== String(activity._id) || !live(response)) throw new Error('Unconfirmed workout start');
      publish(response, scope);
      // Streaming is optional, isolated from write/reconciliation failure, and
      // fenced after scope disposal even when permission resolves much later.
      Promise.resolve().then(() => isCurrent() && videoService.startWorkoutBroadcast(activity._id))
        .then(result => {
          if (isCurrent()) setStreamNotice(result?.started ? { tone: 'live', text: 'Camera live · recording this workout' } : null);
        }).catch(() => {
          if (isCurrent()) setStreamNotice({ tone: 'error', text: 'Workout started, but the camera stream could not start.' });
        });
      if (isCurrent()) await check(scope);
    } catch (error) {
      if (!isCurrent()) return;
      // Only explicit pre-mutation rejections authorize a new attempt. Network,
      // timeout and server errors may follow a committed write: never replay it.
      if ([400, 401, 403, 404].includes(error.response?.status)) {
        operationRef.current = null;
        setPhase('idle'); setNotice('Workout could not start. Please check the workout and try again.');
      } else {
        setPhase('unknown'); setNotice('Start outcome is unknown. Check workout status before starting again.');
      }
    }
  };
  // Keep retries attached to the unresolved operation, not to renders or the
  // checking/unknown transitions. Every attempt is a single-flight GET only.
  const recoveryRef = useRef(null);
  recoveryRef.current = { scope, check, current: () => current(scope) };
  const unresolved = Boolean(operationRef.current && !acknowledged && ['unknown', 'checking'].includes(phase));
  useEffect(() => {
    if (!unresolved) return undefined;
    let disposed = false;
    let timer = null;
    scope.recoveryListeners ||= new Set();
    const generation = scope.generation;
    const eligible = () => !disposed && recoveryRef.current.scope === scope && scope.generation === generation
      && recoveryRef.current.current() && !scope.acknowledged
      && ['unknown', 'checking'].includes(scope.phase);
    const available = () => isForeground() && navigator.onLine !== false;
    const stop = () => {
      if (timer === null) return;
      clearTimeout(timer); timer = null;
      scope.autoTimer = null;
    };
    const arm = (callback, delay) => {
      scope.autoTimer = { cancel: stop };
      timer = setTimeout(() => { stop(); callback(); }, delay);
    };
    const schedule = () => {
      if (!eligible() || !available() || scope.autoTimer || checkingRef.current) return;
      // A real resume event owns this intent; subscriber cleanup transfers
      // only its timer, without losing the deadline or authorizing a new burst.
      if (scope.autoRearmAt != null) {
        arm(() => {
          if (!eligible() || !available()) return;
          scope.autoRearmAt = null;
          scope.autoAttempts = 0;
          scope.autoResumedAt = Date.now();
          schedule();
        }, Math.max(0, scope.autoRearmAt - Date.now()));
        return;
      }
      const attempt = scope.autoAttempts || 0;
      if (attempt >= RECONCILE_DELAYS.length) return;
      arm(() => {
        if (!eligible() || !available()) return;
        recoveryRef.current.check(scope, true);
      }, RECONCILE_DELAYS[attempt]);
    };
    const resume = () => {
      if (!available()) { stop(); return; }
      if (!eligible() || scope.autoTimer || checkingRef.current) return;
      // Repeated online/resume events must not create an unbounded read loop.
      if ((scope.autoAttempts || 0) >= RECONCILE_DELAYS.length) {
        // A resume inside the cooldown still gets recovery without needing a
        // second event. Keep the one-shot deadline in the shared operation.
        scope.autoRearmAt ??= scope.autoResumedAt + 30000;
      }
      schedule();
    };
    scope.recoveryListeners.add(schedule);
    const unsubscribe = subscribeForeground(resume);
    window.addEventListener('online', resume);
    window.addEventListener('offline', resume);
    schedule();
    return () => {
      disposed = true;
      stop();
      scope.recoveryListeners.delete(schedule);
      scope.recoveryListeners.forEach(listener => listener());
      if (!scope.recoveryListeners.size) scope.autoRearmAt = null;
      unsubscribe();
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', resume);
    };
  }, [scope, unresolved, checkingRef]);

  const complete = response => {
    if (!current(scope)) return;
    const row = response?._id && String(response._id) === String(activity._id) && response.status === 'complete'
      ? response : { ...activity, status: 'complete' };
    if (coordinator) coordinator.complete(activity._id, row);
    else { scope.generation += 1; checkingRef.current = false; }
  };
  const displayActivity = acknowledged ? preferAcknowledgedActivity(activity, acknowledged) : activity;
  return { activity: displayActivity, start, complete, check: () => check(), phase, notice,
    disabled: phase !== 'idle' || Boolean(operationRef.current && !acknowledged),
    recovery: Boolean(notice && (acknowledged || phase === 'unknown' || phase === 'checking')) };
}
